import { Vector3 } from 'three';
import { Area } from './area';
import { Load } from './load';

export class LoadSnapper {
  private magnetDistance = 10;

  public isDraggedLoadInCenterOfArea(draggedLoad: Load, area: Area): boolean {
    this.magnetDistance = this.calculateMagnetDistance(draggedLoad);
    return (
      draggedLoad.position.x === 0 &&
      draggedLoad.position.x - this.magnetDistance >= area.x &&
      draggedLoad.position.z - this.magnetDistance >= area.z &&
      draggedLoad.position.x + draggedLoad.width + this.magnetDistance <= area.x2() &&
      draggedLoad.position.z + draggedLoad.length + this.magnetDistance <= area.z2()
    );
  }

  public isAnyLoadCornerNearDraggedLoad(
    draggedLoad: Load,
    loads: Load[],
    areas: Area[],
    mousePosition: Vector3,
  ) {
    let positions: Vector3[] = [];
    loads = loads.filter(load => !load.name.includes('pivot') && !load.name.includes('container'));
    this.magnetDistance = this.calculateMagnetDistance(draggedLoad);
    for (let i = 0; i < loads.length; i++) {
      if (loads[i].visible) {
        let position = [];

        if ((position = this.getCloseCornersForBottomLeftCorner(draggedLoad, loads[i]))) {
          positions = positions.concat(position);
        }
        if ((position = this.getCloseCornersForBottomRightCorner(draggedLoad, loads[i]))) {
          positions = positions.concat(position);
        }
        if ((position = this.getCloseCornersForTopLeftCorner(draggedLoad, loads[i]))) {
          positions = positions.concat(position);
        }
        if ((position = this.getCloseCornersForTopRightCorner(draggedLoad, loads[i]))) {
          positions = positions.concat(position);
        }
      }
    }
    this.sortPositionsByDistanceToLoad(positions, mousePosition);
    return positions.find((position) => this.isNewPositionPossible(draggedLoad, position, areas));
  }

  private calculateMagnetDistance(draggedLoad: Load) {
    return Math.min(draggedLoad.width, draggedLoad.length) / 10;
  }

  private sortPositionsByDistanceToLoad(positions: Vector3[], mousePosition: Vector3) {
    positions.sort(
      (position, position1) =>
        this.calculateDistanceToClosestLoadCorner(mousePosition, position) -
        this.calculateDistanceToClosestLoadCorner(mousePosition, position1),
    );
  }

  private calculateDistanceToClosestLoadCorner(mousePosition: Vector3, position: Vector3) {
    return Math.abs(position.x - mousePosition.x) + Math.abs(position.z - mousePosition.z);
  }

  private isNewPositionPossible(draggedLoad: Load, position: Vector3, areas: Area[]) {
    return !!areas.find((area) => area.canFitLoadWithPosition(draggedLoad, position));
  }

  private getCloseCornersForBottomLeftCorner(load: Load, load1: Load): Vector3[] {
    const position: Partial<Vector3> = {};
    const positions: Partial<Vector3>[] = [];
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z },
        { x: load1.x, z: load1.z2() },
      )
    ) {
      position.x = load1.x;
      position.z = load1.z2();
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z },
        { x: load1.x2(), z: load1.z },
      )
    ) {
      position.x = load1.x2();
      position.z = load1.z;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z },
        { x: load1.x2(), z: load1.z2() },
      )
    ) {
      position.x = load1.x2();
      position.z = load1.z2();
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z },
        { x: load1.x, z: load1.z },
      )
    ) {
      position.x = load1.x;
      position.z = load1.z;
      positions.push(position);
    }
    return positions as Vector3[];
  }

  private getCloseCornersForBottomRightCorner(load: Load, load1: Load): Vector3[] {
    const position: Partial<Vector3> = {};
    const positions: Partial<Vector3>[] = [];
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z + load.length },
        { x: load1.x, z: load1.z },
      )
    ) {
      position.x = load1.x;
      position.z = load1.z - load.length;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z + load.length },
        { x: load1.x2(), z: load1.z },
      )
    ) {
      position.x = load1.x2();
      position.z = load1.z - load.length;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z + load.length },
        { x: load1.x2(), z: load1.z2() },
      )
    ) {
      position.x = load1.x2();
      position.z = load1.z2() - load.length;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x, z: load.position.z + load.length },
        { x: load1.x, z: load1.z2() },
      )
    ) {
      position.x = load1.x;
      position.z = load1.z2() - load.length;
      positions.push(position);
    }
    return positions as Vector3[];
  }

  private getCloseCornersForTopLeftCorner(load: Load, load1: Load): Vector3[] {
    const position: Partial<Vector3> = {};
    const positions: Partial<Vector3>[] = [];
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z },
        { x: load1.x, z: load1.z },
      )
    ) {
      position.x = load1.x - load.width;
      position.z = load1.z;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z },
        { x: load1.x, z: load1.z2() },
      )
    ) {
      position.x = load1.x - load.width;
      position.z = load1.z2();
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z },
        { x: load1.x2(), z: load1.z2() },
      )
    ) {
      position.x = load1.x2() - load.width;
      position.z = load1.z2();
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z },
        { x: load1.x2(), z: load1.z },
      )
    ) {
      position.x = load1.x2() - load.width;
      position.z = load1.z;
      positions.push(position);
    }
    return positions as Vector3[];
  }

  private getCloseCornersForTopRightCorner(load: Load, load1: Load): Vector3[] {
    const position: Partial<Vector3> = {};
    const positions: Partial<Vector3>[] = [];
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z + load.length },
        { x: load1.x, z: load1.z },
      )
    ) {
      position.x = load1.x - load.width;
      position.z = load1.z - load.length;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z + load.length },
        { x: load1.x, z: load1.z2() },
      )
    ) {
      position.x = load1.x - load.width;
      position.z = load1.z2() - load.length;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z + load.length },
        { x: load1.x2(), z: load1.z },
      )
    ) {
      position.x = load1.x2() - load.width;
      position.z = load1.z - load.length;
      positions.push(position);
    }
    if (
      this.arePositionsInMagnetDistance(
        { x: load.position.x + load.width, z: load.position.z + load.length },
        { x: load1.x2(), z: load1.z2() },
      )
    ) {
      position.x = load1.x2() - load.width;
      position.z = load1.z2() - load.length;
      positions.push(position);
    }
    return positions as Vector3[];
  }

  private arePositionsInMagnetDistance(
    position: Partial<Vector3>,
    position1: Partial<Vector3>,
  ): boolean {
    return (
      Math.abs(position.x - position1.x) <= this.magnetDistance &&
      Math.abs(position.z - position1.z) <= this.magnetDistance
    );
  }
}
